import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FaShoppingCart } from 'react-icons/fa';
import productosService from '../../services/productosService';
import { COMMERCE_SOURCES } from '../../services/analyticsService';
import useProductActions from '../../hooks/useProductActions';
import ProductImage from '../products/ProductImage';
import { rutaProducto } from '../../utils/productUrl';

/**
 * Banner del producto destacado: reemplaza la primera sección de la landing
 * (antes HeroSection) cuando un administrador marca un producto como
 * destacado desde el panel. Solo puede haber uno a la vez (ver migración 033
 * y AdminToggleDestacado en el backend).
 *
 * Sin producto destacado no hay nada que mostrar aquí — a diferencia de
 * NovedadesSection/AhiLesDejoPage, que usaban un id fijo, esto depende de una
 * elección editable y puede no haber ninguna. El componente no renderiza nada
 * en ese caso, en vez de caer a un contenido por defecto: así HomePage no
 * necesita saber si hay o no destacado.
 */
export default function FeaturedProductBanner() {
  const [producto, setProducto] = useState(null);
  const { addToCartTracked, buyNow } = useProductActions(
    COMMERCE_SOURCES.HOME_DESTACADO_PRINCIPAL
  );

  useEffect(() => {
    let vivo = true;
    productosService
      .getProductoDestacado()
      .then(p => {
        if (vivo) setProducto(p);
      })
      .catch(() => {
        if (vivo) setProducto(null);
      });
    return () => {
      vivo = false;
    };
  }, []);

  if (!producto) return null;

  const enOferta = producto.Oferta?.toLowerCase() === 'si';
  const stock = Number(producto.cantidad ?? 0);
  const agotado = stock <= 0;

  return (
    <section className="bg-df-surface border-b border-df-border">
      {/* HomePage sube todo con -mt-[navbar-height] para que el fondo pase por
          detrás del navbar transparente; el banner es la primera sección y
          tiene que devolver esa altura en su padding superior. */}
      <div className="max-w-6xl mx-auto px-4 pt-[calc(var(--navbar-height)+2.5rem)] pb-10 md:pt-[calc(var(--navbar-height)+3.5rem)] md:pb-14 grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
        <Link
          to={rutaProducto(producto)}
          state={{ source: COMMERCE_SOURCES.HOME_DESTACADO_PRINCIPAL }}
          aria-label={`Ver ficha de ${producto.Juego}`}
        >
          <ProductImage
            src={producto.Imagen}
            alt={producto.Juego}
            agotado={agotado}
            className="aspect-square w-full rounded-xl p-6 shadow-lg"
          />
        </Link>

        <div>
          <span className="inline-block bg-df-accent/20 text-df-accent text-xs font-bold tracking-wide uppercase px-3 py-1 rounded-full mb-3">
            Destacado
          </span>

          {producto.marca && <p className="text-sm text-df-muted mb-1">{producto.marca}</p>}

          <Link to={rutaProducto(producto)} state={{ source: COMMERCE_SOURCES.HOME_DESTACADO_PRINCIPAL }}>
            <h2 className="font-cinzel text-3xl md:text-4xl font-bold text-df-text hover:text-df-accent transition-colors mb-4">
              {producto.Juego}
            </h2>
          </Link>

          {producto.descripcion && (
            <p className="text-df-muted leading-relaxed mb-6 line-clamp-4">{producto.descripcion}</p>
          )}

          <div className="mb-6">
            {enOferta && producto.Precio_Oferta != null && producto.Precio_Publico != null ? (
              <div>
                <p className="text-df-muted line-through">${producto.Precio_Publico.toLocaleString()}</p>
                <p className="text-df-gold font-bold text-2xl">${producto.Precio_Oferta.toLocaleString()}</p>
              </div>
            ) : producto.Precio_Publico != null ? (
              <p className="text-df-gold font-bold text-2xl">${producto.Precio_Publico.toLocaleString()}</p>
            ) : (
              <p className="text-df-muted font-medium">Consultar</p>
            )}
          </div>

          {agotado ? (
            <button
              disabled
              className="bg-df-surface2 text-df-muted text-sm font-semibold px-6 py-3 rounded cursor-not-allowed"
            >
              AGOTADO
            </button>
          ) : (
            <div className="flex flex-wrap gap-3">
              <button
                onClick={() => addToCartTracked(producto, 1)}
                className="bg-df-accent-strong hover:bg-df-accent-deep text-white text-sm font-semibold px-6 py-3 rounded flex items-center justify-center gap-2 transition-colors duration-200"
              >
                <FaShoppingCart className="text-sm" />
                AGREGAR AL CARRITO
              </button>
              <button
                onClick={() => buyNow(producto, 1)}
                className="bg-df-gold hover:bg-yellow-400 text-df-bg text-sm font-semibold px-6 py-3 rounded flex items-center justify-center gap-2 transition-colors duration-200"
              >
                <span>🛒</span>
                COMPRAR AHORA
              </button>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
